import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { Receipt } from '../../interfaces/sales/receipt.interface';
import { SalesInvoice } from '../../interfaces/sales/salesInvoice.interface';
import { ReceiptService } from '../../services/receipt.service';
import { SalesInvoiceService } from '../../services/salesInvoice.service';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import { formatMoney } from '../../utils/format';

interface Props {
  open: boolean;
  receipt: Receipt;
  onClose: () => void;
  onApplied: (r: Receipt) => void;
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const outstandingOf = (inv: SalesInvoice) => inv.totalAmount - inv.paidAmount - (inv.creditedAmount ?? 0);

export const invoiceOptionLabel = (inv: SalesInvoice): string =>
  `${inv.invoiceNumber ?? 'Draft'} - outstanding ${formatMoney(outstandingOf(inv))}`;

const ApplyReceiptDialog: React.FC<Props> = ({ open, receipt, onClose, onApplied }) => {
  const toast = useToast();
  const [invoices, setInvoices] = useState<SalesInvoice[]>([]);
  const [invoiceId, setInvoiceId] = useState('');
  const [amount, setAmount] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);
  // W3-E: the receipt can be applied in parts; its unapplied balance is amount - allocatedAmount.
  const unapplied = round2(receipt.amount - (receipt.allocatedAmount ?? 0));

  useEffect(() => {
    if (!open || !receipt.customerId) return;
    setInvoiceId('');
    setAmount('');
    SalesInvoiceService.listByCustomer(receipt.customerId)
      .then(list => setInvoices(list.filter(i => i.status === 'ISSUED' || i.status === 'PARTIALLY_PAID')))
      .catch(e => toast.showApiError(e, 'Failed to load invoices'));
  }, [open, receipt.customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = invoices.find(i => i.id === invoiceId);
  const cap = selected ? Math.min(unapplied, outstandingOf(selected)) : unapplied;
  const tooBig = amount !== '' && Number(amount) > cap + 0.005;

  const handleInvoice = (id: string) => {
    setInvoiceId(id);
    const inv = invoices.find(i => i.id === id);
    setAmount(inv ? round2(Math.min(unapplied, outstandingOf(inv))) : '');
  };

  const handleApply = async () => {
    if (!invoiceId || amount === '' || Number(amount) <= 0 || tooBig) return;
    setSaving(true);
    try {
      const updated = await ReceiptService.apply(receipt.id, invoiceId, Number(amount));
      toast.showSuccess('Receipt applied to invoice');
      onApplied(updated);
      onClose();
    } catch (e) {
      toast.showApiError(e, 'Failed to apply receipt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth data-help-page="sales.applyReceipt.page">
      <DialogTitle>Apply to invoice</DialogTitle>
      <DialogContent data-help-section="sales.applyReceipt.section.form">
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            Unapplied balance: {formatMoney(unapplied)}. Apply all or part of it to one invoice; the rest stays on account.
          </Typography>
          <SearchableSelect<SalesInvoice>
            data-help="sales.applyReceipt.invoice"
            label="Open invoice"
            required
            options={invoices}
            value={invoiceId}
            onChange={handleInvoice}
            getOptionLabel={invoiceOptionLabel}
            noOptionsText="No open invoices for this customer"
          />
          <TextField
            data-help="sales.applyReceipt.amount"
            label="Amount to apply"
            type="number"
            required
            value={amount}
            onChange={e => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
            error={tooBig}
            helperText={tooBig ? `At most ${formatMoney(cap)} can be applied to this invoice` : undefined}
            inputProps={{ min: 0, step: '0.01' }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button data-help="sales.applyReceipt.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button data-help="sales.applyReceipt.apply" variant="contained" onClick={handleApply} disabled={saving || !invoiceId || amount === '' || Number(amount) <= 0 || tooBig}>
          {saving ? 'Applying…' : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApplyReceiptDialog;
